import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchAdminCreationDetail } from '../../Reducers/actions/creationsActions';
import { AdminDetailPageSkeleton } from '../../components/Skeletons/BatchScreenSkeletons';
import Icon from '../../components/Icon/Icon';
import { GENRE_LABELS } from '../../constants/genres';
import moment from 'moment';
import { formatEurFromCents } from '../../../helpers/money';
import './AdminCreationDetail.scss';
import '../Admin/Admin.scss';

function safeValue(value) {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'object') return '—';
  if (typeof value === 'number') return value;
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
}

const ENGAGEMENT_ITEMS = [
  { key: 'liked_count', label: 'Liked', icon: 'engagement-liked' },
  { key: 'views_count', label: 'Views', icon: 'engagement-views' },
  { key: 'comments_count', label: 'Comments', icon: 'comment' },
  { key: 'favoured_count', label: 'Favoured', icon: 'star' },
  { key: 'shares_count', label: 'Shares', icon: 'share' },
  { key: 'in_library_count', label: 'In Library', icon: 'library' },
];

const FINANCIAL_ITEMS = [
  { key: 'creation_price', label: 'Creation price', format: formatEurFromCents },
  { key: 'quantity', label: 'Quantity', raw: true },
  { key: 'total_purchases', label: 'Total purchases', raw: true },
  { key: 'total_earnings', label: 'Total earnings', format: formatEurFromCents },
  { key: 'hyk_revenue', label: "HYK's Revenue", format: formatEurFromCents },
  { key: 'stripe_fees', label: 'Stripe fees', format: formatEurFromCents },
  { key: 'abandoned_in_carts', label: 'Abandoned in carts', raw: true },
  { key: 'pending_orders', label: 'Pending orders', raw: true },
  { key: 'total_one_time_contributions_count', label: 'Total One-time contributions', raw: true },
  { key: 'one_time_contributions_amount', label: 'One-time contributions', format: formatEurFromCents },
  { key: 'total_monthly_contributions_count', label: 'Total Monthly contributions', raw: true },
  { key: 'monthly_contributions_amount', label: 'Monthly contributions', format: formatEurFromCents },
];

function AdminCreationDetail() {
  const { creation_id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!creation_id) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetchAdminCreationDetail(creation_id);
        if (!cancelled && response?.success && response?.data) {
          setData(response.data);
        } else if (!cancelled) {
          setError('Creation not found');
        }
      } catch (err) {
        if (!cancelled) {
          setError('Failed to load creation details');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [creation_id]);

  if (loading) {
    return (
      <AdminDetailPageSkeleton className="admin-creation-detail" />
    );
  }

  if (error || !data) {
    return (
      <div className="admin-creation-detail">
        <div className="admin-creation-detail-error">
          <Icon alias="alert" color="#FF3232" width={64} />
          <h2>{error || 'Creation not found'}</h2>
          <button type="button" className="back-btn" onClick={() => navigate('/admin/creation')}>
            Back to Creations
          </button>
        </div>
      </div>
    );
  }

  const title = data.title || data.creation_title || '—';
  const creatorId = data.creator_id || data.creator?.user_id;
  const creatorUsername = data.creator_username ?? data.creator?.userName ?? '';
  const displayUsername = creatorUsername.startsWith('@') ? creatorUsername : `@${creatorUsername}`;
  const type = data.type || data.creation_type || '';
  const category = data.category || '';
  const categoryLabel = GENRE_LABELS[category?.toLowerCase()] || category || '—';
  const isPaid = data.monetisation === 'paid' || data.monetization === 'paid' || data.is_paid;
  const status = data.status || 'active';

  return (
    <div className="admin-creation-detail">
      <div className="creation-detail-header">
        <div className="breadcrumb">
          <button type="button" className="breadcrumb-link" onClick={() => navigate('/admin/creation')}>
            Creations {data.creations_total_count != null ? `[${Number(data.creations_total_count).toLocaleString()}]` : ''}
          </button>
          <span className="breadcrumb-separator">/</span>
          <span className="breadcrumb-current">Creation details</span>
        </div>
        <div className="creation-detail-header-right">
          <div className={`status-tag status-${(status || 'active').toLowerCase()}`}>
            <span>{status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Active'}</span>
            <Icon alias="chevron-down" width={14} height={14} color="currentColor" />
          </div>
          <button
            type="button"
            className="view-creation-btn"
            onClick={() => window.open(`/creations/${creation_id}`, '_blank')}
          >
            VIEW CREATION
          </button>
        </div>
      </div>

      <div className="creation-detail-grid">
        <div className="creation-summary-card">
          <h1 className="creation-detail-title">{title}</h1>
          <p className="creation-detail-creator">
            by{' '}
            {creatorId ? (
              <button
                type="button"
                className="creator-link"
                onClick={() => navigate(`/admin/users/${creatorId}`)}
              >
                {displayUsername}
              </button>
            ) : (
              displayUsername || '—'
            )}
          </p>
          <div className="creation-tags">
            {type && <span className="tag tag-type">{type}</span>}
            <span className={`tag tag-monetisation ${isPaid ? 'paid' : 'free'}`}>
              {isPaid ? 'Paid' : 'Free'}
            </span>
            {(isPaid || data.monetised) && (
              <span className="tag tag-monetised">Monetised &gt;</span>
            )}
          </div>
          <dl className="creation-meta">
            <div className="meta-row">
              <dt>Category</dt>
              <dd>{categoryLabel}</dd>
            </div>
            <div className="meta-row">
              <dt>Cultural area</dt>
              <dd>{safeValue(data.cultural_area) || '—'}</dd>
            </div>
            <div className="meta-row">
              <dt>Downloads</dt>
              <dd>{safeValue(data.downloads) ?? 0}</dd>
            </div>
            <div className="meta-row">
              <dt>Created at</dt>
              <dd>{data.created_at ? moment(data.created_at).format('DD/MM/YYYY HH:mm') : '—'}</dd>
            </div>
            <div className="meta-row">
              <dt>Updated at</dt>
              <dd>{data.updated_at ? moment(data.updated_at).format('DD/MM/YYYY HH:mm') : '—'}</dd>
            </div>
          </dl>
        </div>

        <div className="engagement-card">
          <h3 className="engagement-card-title">Engagement</h3>
          <div className="engagement-metrics">
            {ENGAGEMENT_ITEMS.map(({ key, label, icon }) => (
              <div key={key} className="engagement-item">
                <Icon alias={icon} width={56} height={56} stroke="#FF8CDC" strokeWidth={1} color="#FF8CDC" />
                <span className="engagement-value">{safeValue(data[key]) ?? 0}</span>
                <span className="engagement-label">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="financial-grid">
        {FINANCIAL_ITEMS.map(({ key, label, format, raw }) => (
          <div key={key} className="financial-card">
            <span className="financial-label">{label}</span>
            <span className="financial-value">
              {raw ? safeValue(data[key]) : (format ? format(data[key]) : safeValue(data[key]))}
            </span>
          </div>
        ))}
      </div>

      <div className="creation-tables-section">
        <div className="creation-table-block">
          <h3 className="creation-table-title">Buyers</h3>
          <div className="creation-table-wrapper">
            <table className="creation-detail-table">
              <thead>
                <tr>
                  <th>Buyers</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {(data.buyers || []).length === 0 ? (
                  <tr>
                    <td colSpan={2}>No buyers yet</td>
                  </tr>
                ) : (
                  (data.buyers || []).map((row, idx) => (
                    <tr key={row.user_id || row.buyer_id || idx}>
                      <td>
                        {row.user_id ? (
                          <button
                            type="button"
                            className="table-username-link"
                            onClick={() => navigate(`/admin/users/${row.user_id}`)}
                          >
                            {row.username?.startsWith('@') ? row.username : `@${row.username || ''}`}
                          </button>
                        ) : (
                          row.username?.startsWith('@') ? row.username : `@${row.username || ''}`
                        )}
                      </td>
                      <td>
                        <span className={`status-dot status-${(row.status || 'completed').toLowerCase()}`}>
                          • {row.status ? row.status.charAt(0).toUpperCase() + row.status.slice(1) : 'Completed'}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="creation-table-block">
          <h3 className="creation-table-title">Contributors</h3>
          <div className="creation-table-wrapper">
            <table className="creation-detail-table">
              <thead>
                <tr>
                  <th>One-time Contributors</th>
                  <th>Amount</th>
                  <th>Monthly Contributors</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {(data.one_time_contributors || []).length === 0 && (data.monthly_contributors || []).length === 0 ? (
                  <tr>
                    <td colSpan={4}>No contributors yet</td>
                  </tr>
                ) : (
                  (() => {
                    const oneTime = data.one_time_contributors || [];
                    const monthly = data.monthly_contributors || [];
                    const maxRows = Math.max(oneTime.length, monthly.length, 1);
                    return Array.from({ length: maxRows }, (_, i) => {
                      const ot = oneTime[i];
                      const mon = monthly[i];
                      return (
                        <tr key={i}>
                          <td>
                            {ot?.user_id ? (
                              <button
                                type="button"
                                className="table-username-link"
                                onClick={() => navigate(`/admin/users/${ot.user_id}`)}
                              >
                                {ot.username?.startsWith('@') ? ot.username : `@${ot.username || ''}`}
                              </button>
                            ) : (
                              ot?.username?.startsWith('@') ? ot.username : ot?.username ? `@${ot.username}` : '—'
                            )}
                          </td>
                          <td>{ot?.amount != null ? formatEurFromCents(ot.amount) : '—'}</td>
                          <td>
                            {mon?.user_id ? (
                              <button
                                type="button"
                                className="table-username-link"
                                onClick={() => navigate(`/admin/users/${mon.user_id}`)}
                              >
                                {mon.username?.startsWith('@') ? mon.username : `@${mon.username || ''}`}
                              </button>
                            ) : (
                              mon?.username?.startsWith('@') ? mon.username : mon?.username ? `@${mon.username}` : '—'
                            )}
                          </td>
                          <td>{mon?.amount != null ? formatEurFromCents(mon.amount) : '—'}</td>
                        </tr>
                      );
                    });
                  })()
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminCreationDetail;
